import { computed, inject, onMounted } from 'vue';
import { useWalletStore } from '@/stores/wallet';
import { currencyConfigFormat } from '@/helpers/formatCurrency';
import { formatToLocalTime } from '@/shared/common';
import { useCompanyAccountsCatalog } from '@/shared/additionalAccounts/composables/useCompanyAccountsCatalog';
import {
  initialValuesTransactionProvider,
  transactionProviderKey,
} from '../constants/transactionProvider.constant';
import {
  BETWEEN_ACCOUNTS_SUCCESS_AVAILABLE_BALANCE_LABEL,
  BETWEEN_ACCOUNTS_SUCCESS_DATE_FORMAT,
  BETWEEN_ACCOUNTS_SUCCESS_DATE_LABEL,
  BETWEEN_ACCOUNTS_SUCCESS_FROM_LABEL,
  BETWEEN_ACCOUNTS_SUCCESS_TO_LABEL,
  TRANSACTION_TYPE_BETWEEN_ACCOUNTS,
} from '../constants/betweenAccounts.constant';
import type {
  BetweenAccountsSuccessDetail,
  UseBetweenAccountsSuccessReturn,
} from '../types/betweenAccountsSuccess.type';

/**
 * Detalle del success de un traspaso entre cuentas propias. El contexto lo marca el payload que
 * deja `useBetweenAccountsQuoter` en el provider (`transactionType` = `P2P_ADDITIONAL_ACCOUNT`).
 * El saldo de origen que trae el flujo es de ANTES del traspaso: al montar se recargan wallets y
 * catálogo, y la fila se actualiza sola cuando el catálogo resuelve la cuenta de origen.
 */
export function useBetweenAccountsSuccess(): UseBetweenAccountsSuccessReturn {
  const { transactionPayload, preTransactionResponse, originWallet, destinationWallet } = inject(
    transactionProviderKey,
    initialValuesTransactionProvider
  );
  const walletStore = useWalletStore();
  const { loadCatalog, findWalletByAccountId } = useCompanyAccountsCatalog();

  const isBetweenAccountsContext = computed<boolean>(
    () => transactionPayload.value?.transactionType === TRANSACTION_TYPE_BETWEEN_ACCOUNTS
  );

  const originAvailableBalance = computed<number>(() => {
    const originAccountId = transactionPayload.value?.origin?.accountId;
    const refreshedOrigin = originAccountId ? findWalletByAccountId(String(originAccountId)) : undefined;

    return (refreshedOrigin ?? originWallet.value)?.balance?.availableBalance ?? 0;
  });

  function formatAmount(amount: number, currencyCode: string): string {
    return currencyConfigFormat({ amount, currencyCode, hasCode: true });
  }

  const details = computed<BetweenAccountsSuccessDetail[]>(() => {
    const originCurrency = originWallet.value?.currency ?? '';
    const destinationCurrency = destinationWallet.value?.currency || originCurrency;
    const { dateCreated, destinationAmount } = preTransactionResponse.value ?? {};

    return [
      {
        id: 'available-balance',
        label: BETWEEN_ACCOUNTS_SUCCESS_AVAILABLE_BALANCE_LABEL,
        value: formatAmount(originAvailableBalance.value, originCurrency),
      },
      {
        id: 'date',
        label: BETWEEN_ACCOUNTS_SUCCESS_DATE_LABEL,
        value: dateCreated ? formatToLocalTime(dateCreated, BETWEEN_ACCOUNTS_SUCCESS_DATE_FORMAT) : '',
      },
      {
        id: 'sent',
        label: BETWEEN_ACCOUNTS_SUCCESS_FROM_LABEL,
        value: formatAmount(transactionPayload.value?.origin?.amount ?? 0, originCurrency),
      },
      {
        id: 'received',
        label: BETWEEN_ACCOUNTS_SUCCESS_TO_LABEL,
        value: formatAmount(destinationAmount ?? transactionPayload.value?.destiny?.amount ?? 0, destinationCurrency),
      },
    ];
  });

  onMounted(() => {
    if (!isBetweenAccountsContext.value) return;
    Promise.all([walletStore.setWallets(), loadCatalog()]);
  });

  return { isBetweenAccountsContext, details };
}
